import React, { useRef, useState } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import CommonButton from './CommonButton';
import CommonModal from './CommonModal';
import CommonBadge from './CommonBadge';
import { collectSystemReport, type SystemReport } from '../utils/systemReport';

// Fixed status ramp (good -> warning -> serious/high -> critical) — never themed,
// always paired with a label so a status color never carries meaning alone.
const STATUS_COLOR = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
  unknown: '#c3c2b7',
} as const;

type StatusKey = keyof typeof STATUS_COLOR;

const formatMs = (value: number | null): string => (value === null ? 'n/a' : `${Math.round(value).toLocaleString()} ms`);

const severityBadgeType = (severity: string): 'closed' | 'pending' | 'rejected' | 'neutral' => {
  if (severity === 'critical' || severity === 'high') return 'rejected';
  if (severity === 'moderate') return 'pending';
  if (severity === 'low') return 'neutral';
  return 'closed';
};

const ReportRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.4 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={700}>{value}</Typography>
  </Stack>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1a3c42', mt: 1 }}>
    {children}
  </Typography>
);

// A "single ratio against a limit" reads better as a meter than as a bar mixing
// incompatible units (ms vs. an unitless layout-shift score).
const MetricMeter: React.FC<{ label: string; displayValue: string; ratio: number | null; status: StatusKey }> = ({
  label,
  displayValue,
  ratio,
  status,
}) => {
  const color = STATUS_COLOR[status];
  const percent = ratio === null ? 0 : Math.min(1, Math.max(0, ratio)) * 100;

  return (
    <Box sx={{ py: 0.5 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.4 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={700} sx={{ color }}>{displayValue}</Typography>
      </Stack>
      <Box sx={{ height: 8, borderRadius: 4, backgroundColor: '#e1e0d9', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${percent}%`,
            backgroundColor: color,
            borderRadius: 4,
            transition: 'width 0.5s ease',
          }}
        />
      </Box>
    </Box>
  );
};

interface VitalSpec {
  key: keyof SystemReport['webVitals'];
  label: string;
  good: number;
  poor: number;
  format: (value: number) => string;
}

const VITAL_SPECS: VitalSpec[] = [
  { key: 'lcp', label: 'Largest Contentful Paint', good: 2500, poor: 4000, format: (v) => `${Math.round(v).toLocaleString()} ms` },
  { key: 'fcp', label: 'First Contentful Paint', good: 1800, poor: 3000, format: (v) => `${Math.round(v).toLocaleString()} ms` },
  { key: 'ttfb', label: 'Time to First Byte', good: 800, poor: 1800, format: (v) => `${Math.round(v).toLocaleString()} ms` },
  { key: 'fid', label: 'First Input Delay', good: 100, poor: 300, format: (v) => `${Math.round(v).toLocaleString()} ms` },
  { key: 'cls', label: 'Cumulative Layout Shift', good: 0.1, poor: 0.25, format: (v) => v.toFixed(3) },
];

const vitalStatus = (value: number | null, good: number, poor: number): StatusKey => {
  if (value === null) return 'unknown';
  if (value <= good) return 'good';
  if (value >= poor) return 'critical';
  return 'warning';
};

interface SeverityTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: { severity: string; count: number; color: string } }>;
}

const SeverityTooltip: React.FC<SeverityTooltipProps> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;

  return (
    <Box sx={{ backgroundColor: '#fff', border: '1px solid #e1e0d9', borderRadius: 1.5, px: 1.25, py: 0.75, boxShadow: '0 4px 16px rgba(11,11,11,0.12)' }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: row.color }} />
        <Typography variant="caption" sx={{ color: '#52514e' }}>
          {row.severity}: <strong style={{ color: '#0b0b0b' }}>{row.count}</strong>
        </Typography>
      </Stack>
    </Box>
  );
};

const SystemReportButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [report, setReport] = useState<SystemReport | null>(null);
  const reportContentRef = useRef<HTMLDivElement | null>(null);

  const handleOpen = async (): Promise<void> => {
    setOpen(true);
    setLoading(true);
    const nextReport = await collectSystemReport();
    setReport(nextReport);
    setLoading(false);
  };

  const handleDownload = (): void => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `system-report-${report.generatedAt.slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async (): Promise<void> => {
    if (!report || !reportContentRef.current) return;

    setGeneratingPdf(true);
    try {
      const canvas = await html2canvas(reportContentRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      });

      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 28;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;
      const pxPerPt = canvas.width / usableWidth;
      const pageContentHeightPx = usableHeight * pxPerPt;

      pdf.setFontSize(15);
      pdf.text('System Report', margin, margin);
      pdf.setFontSize(9);
      pdf.setTextColor('#52514e');
      pdf.text(`Generated ${new Date(report.generatedAt).toLocaleString()} · ${report.environment} · v${report.appVersion}`, margin, margin + 14);

      let renderedPx = 0;
      let pageIndex = 0;

      while (renderedPx < canvas.height) {
        if (pageIndex > 0) {
          pdf.addPage();
        }

        const sliceHeightPx = Math.min(pageContentHeightPx, canvas.height - renderedPx);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext('2d');
        ctx?.drawImage(canvas, 0, renderedPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

        const imgData = sliceCanvas.toDataURL('image/png');
        const sliceHeightPt = sliceHeightPx / pxPerPt;
        const y = pageIndex === 0 ? margin + 24 : margin;
        pdf.addImage(imgData, 'PNG', margin, y, usableWidth, sliceHeightPt);

        renderedPx += sliceHeightPx;
        pageIndex += 1;
      }

      pdf.save(`system-report-${report.generatedAt.slice(0, 10)}.pdf`);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const severityChartData = report
    ? [
        { severity: 'Critical', count: report.dependencySnapshot.summary.critical, color: STATUS_COLOR.critical },
        { severity: 'High', count: report.dependencySnapshot.summary.high, color: STATUS_COLOR.serious },
        { severity: 'Moderate', count: report.dependencySnapshot.summary.moderate, color: STATUS_COLOR.warning },
        { severity: 'Low', count: report.dependencySnapshot.summary.low, color: STATUS_COLOR.good },
      ]
    : [];

  return (
    <>
      <CommonButton
        label="System Report"
        size="small"
        variant="outlined"
        startIcon={<AssessmentRoundedIcon />}
        onClick={() => {
          void handleOpen();
        }}
      />

      <CommonModal
        open={open}
        title="System Report"
        onClose={() => setOpen(false)}
        maxWidth="md"
        actions={
          <>
            <CommonButton label="Close" variant="outlined" onClick={() => setOpen(false)} />
            <CommonButton
              label="Download JSON"
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              disabled={!report}
              onClick={handleDownload}
            />
            <CommonButton
              label={generatingPdf ? 'Generating...' : 'Download PDF'}
              startIcon={<PictureAsPdfRoundedIcon />}
              disabled={!report || generatingPdf}
              onClick={() => {
                void handleDownloadPdf();
              }}
            />
          </>
        }
      >
        {loading || !report ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Collecting live metrics...
          </Typography>
        ) : (
          <Stack spacing={0.5} ref={reportContentRef} sx={{ backgroundColor: '#ffffff' }}>
            <Typography variant="caption" color="text.secondary">
              Generated {new Date(report.generatedAt).toLocaleString()} · {report.environment} · v{report.appVersion}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                columnGap: 4,
                mt: 0.5,
              }}
            >
              <Box>
                <SectionTitle>Page Load (Web Vitals)</SectionTitle>
                <Stack spacing={0.25}>
                  {VITAL_SPECS.map((spec) => {
                    const value = report.webVitals[spec.key];
                    const status = vitalStatus(value, spec.good, spec.poor);
                    return (
                      <MetricMeter
                        key={spec.key}
                        label={spec.label}
                        displayValue={value === null ? (spec.key === 'fid' ? 'no interaction yet' : 'n/a') : spec.format(value)}
                        ratio={value === null ? null : value / spec.poor}
                        status={status}
                      />
                    );
                  })}
                </Stack>
              </Box>

              <Box>
                <SectionTitle>Resources & Lazy Loading</SectionTitle>
                <ReportRow label="DOM content loaded" value={formatMs(report.resources.domContentLoadedMs)} />
                <ReportRow label="Full page load" value={formatMs(report.resources.fullLoadMs)} />
                <ReportRow label="Resources loaded this session" value={report.resources.totalResources} />
                <ReportRow label="JS chunks loaded" value={report.resources.scriptChunkCount} />
                <ReportRow label="Data transferred" value={`${report.resources.totalTransferredKb} KB`} />
                <ReportRow label="Lazy-loaded routes in app" value={report.lazyLoadedRouteCount} />
              </Box>
            </Box>

            <Divider sx={{ mt: 1.5 }} />
            <SectionTitle>Security — live checks</SectionTitle>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
              <CommonBadge label={report.security.isHttps ? 'HTTPS enforced' : 'Not HTTPS'} type={report.security.isHttps ? 'closed' : 'rejected'} />
              <CommonBadge label={report.security.hasMixedContent ? 'Mixed content found' : 'No mixed content'} type={report.security.hasMixedContent ? 'rejected' : 'closed'} />
              <CommonBadge label={report.security.authTokenInLocalStorage ? 'Auth token in localStorage' : 'No token in localStorage'} type={report.security.authTokenInLocalStorage ? 'pending' : 'closed'} />
            </Stack>
            {report.security.authTokenInLocalStorage ? (
              <Typography variant="caption" color="text.secondary">
                Tokens in localStorage are readable by any script on the page (XSS risk). Consider httpOnly cookies if that's ever revisited.
              </Typography>
            ) : null}

            <Divider sx={{ mt: 1.5 }} />
            <SectionTitle>Security — dependency audit snapshot</SectionTitle>
            <Typography variant="caption" color="text.secondary">
              As of {new Date(report.dependencySnapshot.generatedAt).toLocaleString()} · run{' '}
              <Box component="code" sx={{ px: 0.5, backgroundColor: '#f0f5f6', borderRadius: 0.5 }}>npm run security:audit</Box>{' '}
              to refresh
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr' },
                columnGap: 3,
                alignItems: 'center',
                mt: 0.5,
              }}
            >
              <Box sx={{ height: 190 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={severityChartData} layout="vertical" margin={{ top: 8, right: 34, bottom: 8, left: 4 }} barCategoryGap="22%">
                    <XAxis type="number" hide domain={[0, (dataMax: number) => Math.max(dataMax, 4)]} />
                    <YAxis
                      type="category"
                      dataKey="severity"
                      width={76}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#52514e' }}
                    />
                    <Tooltip content={<SeverityTooltip />} cursor={{ fill: 'rgba(11,11,11,0.04)' }} />
                    <Bar dataKey="count" barSize={20} radius={[0, 4, 4, 0]} minPointSize={3} isAnimationActive animationDuration={600}>
                      {severityChartData.map((entry) => (
                        <Cell key={entry.severity} fill={entry.color} />
                      ))}
                      <LabelList dataKey="count" position="right" style={{ fill: '#0b0b0b', fontSize: 12, fontWeight: 700 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {report.dependencySnapshot.directPackageFindings.length > 0 ? (
                <Stack spacing={0.5}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Direct, production-relevant packages</Typography>
                  {report.dependencySnapshot.directPackageFindings.map((finding) => (
                    <Stack key={finding.name} direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption">{finding.name}</Typography>
                      <CommonBadge label={`${finding.severity}${finding.fixAvailable ? '' : ' · no fix yet'}`} type={severityBadgeType(finding.severity)} />
                    </Stack>
                  ))}
                </Stack>
              ) : null}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
              {report.dependencySnapshot.note}
            </Typography>
          </Stack>
        )}
      </CommonModal>
    </>
  );
};

export default SystemReportButton;
